import { Instagram, Sms, SidebarBottom, Call, User } from '@/components/icons';
import { Checkbox } from '@/components/ui/checkbox';
import CollapsibleSection from '@/components/ui/collapsible-section';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { useAuth } from '@/context/AuthContext';
import { summarizeFooter } from '../summarize';
import { InlineProfileField } from './InlineProfileField';
import type { FooterCustomizations } from '@/components/preview/types';

interface FooterSectionProps {
    open: boolean;
    onToggle: () => void;
    config: FooterCustomizations;
    updateConfig: (updates: Partial<FooterCustomizations>) => void;
    hasPhone: boolean;
    hasInstagram: boolean;
}

export function FooterSection({
    open,
    onToggle,
    config,
    updateConfig,
    hasPhone,
    hasInstagram,
}: FooterSectionProps) {
    const { user } = useAuth();

    return (
        <CollapsibleSection
            title="Rodapé"
            testId="customize-section-footer"
            icon={SidebarBottom}
            summary={summarizeFooter(config, { hasPhone, hasInstagram })}
            open={open}
            onToggle={onToggle}
            contentClassName="space-y-2"
        >
            {/* Phone */}
            <div className="flex items-start gap-3 rounded-lg border border-border p-3 transition-colors hover:bg-surface">
                <Checkbox
                    id="show-phone"
                    checked={hasPhone && config.phone_number_visible !== false}
                    disabled={!hasPhone}
                    onCheckedChange={(checked) =>
                        updateConfig({
                            phone_number_visible: !!checked,
                        })
                    }
                    className="mt-0.5"
                />
                <div className="flex-1">
                    <Label
                        htmlFor="show-phone"
                        className={`flex items-center gap-2 text-sm font-medium ${
                            !hasPhone ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'
                        }`}
                    >
                        <Call className="size-4 text-muted-foreground" />
                        Telefone
                    </Label>
                    <InlineProfileField
                        testId="customize-profile-phone"
                        display={user?.phone ?? ''}
                        emptyLabel="Adicionar telefone"
                        initialDraft={{ phone: user?.phone ?? '' }}
                        successMessage="Telefone atualizado"
                        renderFields={(draft, set) => (
                            <Input
                                autoFocus
                                value={draft.phone ?? ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    set({ phone: e.target.value })
                                }
                                placeholder="+55 (11) 98765-4321"
                                className="text-xs"
                            />
                        )}
                    />
                </div>
            </div>

            {/* Name */}
            <div className="flex items-start gap-3 rounded-lg border border-border p-3 transition-colors hover:bg-surface">
                <Checkbox
                    id="show-footer-name"
                    checked={config.psychologist_name_visible !== false}
                    onCheckedChange={(checked) =>
                        updateConfig({
                            psychologist_name_visible: !!checked,
                        })
                    }
                    className="mt-0.5"
                />
                <div className="flex-1">
                    <Label
                        htmlFor="show-footer-name"
                        className="flex cursor-pointer items-center gap-2 text-sm font-medium"
                    >
                        <User className="size-4 text-muted-foreground" />
                        Nome
                    </Label>
                </div>
            </div>

            {/* Email */}
            <div className="flex items-start gap-3 rounded-lg border border-border p-3 transition-colors hover:bg-surface">
                <Checkbox
                    id="show-email"
                    checked={config.email_visible !== false}
                    onCheckedChange={(checked) =>
                        updateConfig({
                            email_visible: !!checked,
                        })
                    }
                    className="mt-0.5"
                />
                <div className="flex-1">
                    <Label
                        htmlFor="show-email"
                        className="flex cursor-pointer items-center gap-2 text-sm font-medium"
                    >
                        <Sms className="size-4 text-muted-foreground" />
                        E-mail
                    </Label>
                </div>
            </div>

            {/* Instagram */}
            <div className="flex items-start gap-3 rounded-lg border border-border p-3 transition-colors hover:bg-surface">
                <Checkbox
                    id="show-instagram"
                    checked={hasInstagram && config.instagram_visible !== false}
                    disabled={!hasInstagram}
                    onCheckedChange={(checked) =>
                        updateConfig({
                            instagram_visible: !!checked,
                        })
                    }
                    className="mt-0.5"
                />
                <div className="flex-1">
                    <Label
                        htmlFor="show-instagram"
                        className={`flex items-center gap-2 text-sm font-medium ${
                            !hasInstagram ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'
                        }`}
                    >
                        <Instagram className="size-4 text-muted-foreground" />
                        Instagram
                    </Label>
                    <InlineProfileField
                        testId="customize-profile-instagram"
                        display={user?.instagram ?? ''}
                        emptyLabel="Adicionar Instagram"
                        initialDraft={{ instagram: user?.instagram ?? '' }}
                        successMessage="Instagram atualizado"
                        renderFields={(draft, set) => (
                            <Input
                                autoFocus
                                value={draft.instagram ?? ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    set({ instagram: e.target.value })
                                }
                                placeholder="@seuusuario"
                                className="text-xs"
                            />
                        )}
                    />
                </div>
            </div>

            {/* Footer Padding */}
            <div className="space-y-2 rounded-lg border border-border bg-card p-3">
                <Label className="text-sm font-medium">Espaçamento do rodapé</Label>
                <div className="space-y-2">
                    <Slider
                        value={[config.padding || 12]}
                        onValueChange={(value: number[]) => updateConfig({ padding: value[0] })}
                        min={8}
                        max={24}
                        step={2}
                        className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Compacto (8px)</span>
                        <span className="font-medium">{config.padding || 12}px</span>
                        <span>Grande (24px)</span>
                    </div>
                </div>
            </div>
        </CollapsibleSection>
    );
}
